/**
 * SYNC RULES — useNotifications
 * ─────────────────────────────────────────────────
 * 1. Data fetched via useQuery with queryKey ["notifications", userId]
 * 2. All mutations use useMutation with onSuccess → invalidateQueries
 * 3. Realtime subscription for INSERT events (filtered by user_id) triggers invalidation
 * 4. Legacy window event "refetch-notifications" still supported for backward compat
 */

import { useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  meta: Record<string, unknown>;
  read: boolean;
  snoozed_until: string | null;
  created_at: string;
}

const NOTIFICATIONS_KEY = (userId: string | null) => ["notifications", userId] as const;

export function useNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const qKey = NOTIFICATIONS_KEY(user?.id ?? null);

  const { data: notifications = [], isLoading: loading } = useQuery({
    queryKey: qKey,
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      const now = new Date().toISOString();
      const { data } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user!.id)
        .eq("read", false)
        .or(`snoozed_until.is.null,snoozed_until.lt.${now}`)
        .order("created_at", { ascending: false })
        .limit(20);
      return (data as unknown as Notification[]) || [];
    },
  });

  // Realtime subscription for new notifications
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`notifications-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: qKey });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient, qKey]);

  // Legacy compat: listen for programmatic refetch requests
  useEffect(() => {
    const handler = () => queryClient.invalidateQueries({ queryKey: qKey });
    window.addEventListener("refetch-notifications", handler);
    return () => window.removeEventListener("refetch-notifications", handler);
  }, [queryClient, qKey]);

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from("notifications").update({ read: true } as any).eq("id", id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qKey });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      if (!user) return;
      await supabase.from("notifications").update({ read: true } as any).eq("user_id", user.id).eq("read", false);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qKey });
    },
  });

  const snoozeMutation = useMutation({
    mutationFn: async ({ id, hours }: { id: string; hours: number }) => {
      const until = new Date(Date.now() + hours * 3600_000).toISOString();
      await supabase.from("notifications").update({ snoozed_until: until } as any).eq("id", id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qKey });
    },
  });

  const markRead = useCallback((id: string) => markReadMutation.mutate(id), [markReadMutation]);
  const markAllRead = useCallback(() => markAllReadMutation.mutate(), [markAllReadMutation]);
  const snooze = useCallback((id: string, hours: number) => snoozeMutation.mutate({ id, hours }), [snoozeMutation]);
  const refetch = useCallback(() => queryClient.invalidateQueries({ queryKey: qKey }), [queryClient, qKey]);

  const unreadCount = notifications.length;

  return { notifications, unreadCount, loading, markRead, markAllRead, snooze, refetch };
}
